import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { fetchPost, fetchPosts } from "../lib/api";
import type { Post } from "../types/blog";

function extractBody(html: string): string {
  // Extract any <style> tags from the full HTML
  const styleMatches = html.match(/<style[^>]*>([\s\S]*?)<\/style>/gi);
  const styles = styleMatches ? styleMatches.join("\n") : "";

  // Extract the content inside the <body> tag
  // const bodyMatch = html.match(/<body[^>]*>([\s\S]*?)<\/body>/i);
  // const bodyContent = bodyMatch ? bodyMatch[1] : html;

  // Combine the style tags with the body content
  return `${styles}\n`;
}

function sanitizeHTML(html: string): string {
  return html
    .replace(/<script[\s\S]*?<\/script>/gi, "")
    .replace(/<nav[\s\S]*?<\/nav>/gi, "")
    .replace(/<footer[\s\S]*?<\/footer>/gi, "");
}

function formatContent(content: string): string {
  if (!content) return "";
  const isFullHTML = /<!DOCTYPE html>/i.test(content);
  let html = isFullHTML ? extractBody(content) : content;
  return sanitizeHTML(html);
}

function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState<Post | null>(null);
  const [blogPosts, setPosts] = useState<Post[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (slug) {
      fetchPost(slug)
        .then((post) => {
          setPost(post);
          setLoading(false);
        })
        .catch(() => setLoading(false));
    }
  }, [slug]);

  useEffect(() => {
    fetchPosts()
      .then(setPosts)
      .catch((err) => {
        console.error("❌ Error in BlogPage:", err);
      })
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-gray-600">Loading post...</p>
      </div>
    );
  }

  if (!post || !post.content) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-800 mb-4">
            Post Not Found
          </h2>
          <Link to="/blog" className="text-primary-600 hover:text-primary-700">
            ← Back to Blog
          </Link>
        </div>
      </div>
    );
  }

  // Compute related posts based on the first category (if available)
  const currentCategory =
    post.categories && post.categories.length > 0 ? post.categories[0] : "";
  const relatedPosts = blogPosts
    .filter(
      (p) =>
        p.slug !== post.slug &&
        p.categories &&
        p.categories.includes(currentCategory)
    )
    .slice(0, 3);

  return (
    <article className="py-16 bg-gray-50">
      <div className="container mx-auto px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
          {/* Main Content */}
          <div className="lg:col-span-2">
            <div className="max-w-4xl mx-auto bg-white shadow-lg rounded-2xl p-8">
              {/* Categories Tags */}
              <div className="mb-6 flex flex-wrap gap-2">
                {post.categories?.map((category, index) => (
                  <span
                    key={index}
                    className="inline-block px-4 py-1 bg-primary-100 text-primary-800 text-sm font-medium rounded-full shadow-sm"
                  >
                    {category}
                  </span>
                ))}
              </div>

              {/* Featured Image */}
              {post?.image && (
                <div className="mb-8">
                  <img
                    src={post?.image}
                    alt={post.title}
                    className="w-full h-80 object-cover rounded-xl shadow-md"
                  />
                </div>
              )}
              {/* Article Content */}
              <div className="prose prose-lg max-w-none text-gray-800">
                <div
                  dangerouslySetInnerHTML={{
                    __html: formatContent(`${post?.content || ""} `),
                  }}
                />
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-1">
            {/* Related Articles */}
            <div className="bg-gray-50 rounded-lg p-6 mb-8 shadow-md">
              <h3 className="text-xl font-bold mb-4">Related Articles</h3>
              <div className="space-y-6">
                {relatedPosts.map((relatedPost) => (
                  <div key={relatedPost.slug} className="flex space-x-4">
                    <img
                      src={relatedPost.image}
                      alt={relatedPost.title}
                      className="w-20 h-20 object-cover rounded-md"
                    />
                    <div>
                      <h4 className="font-medium mb-1">
                        <Link
                          to={`/blog/${relatedPost.slug}`}
                          className="hover:text-primary"
                        >
                          {relatedPost.title}
                        </Link>
                      </h4>
                      <span className="text-sm text-gray-500">
                        {relatedPost.date}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Categories */}
            <div className="bg-gray-50 rounded-lg p-6 mb-8 shadow-md">
              <h3 className="text-xl font-bold mb-4">Categories</h3>
              <div className="space-y-2">
                <a
                  href="#"
                  className="block py-2 px-4 bg-primary/10 text-primary rounded-md hover:bg-primary/20"
                >
                  Nutrition (12)
                </a>
                <a
                  href="#"
                  className="block py-2 px-4 bg-gray-100 rounded-md hover:bg-gray-200"
                >
                  Sustainability (8)
                </a>
                <a
                  href="#"
                  className="block py-2 px-4 bg-gray-100 rounded-md hover:bg-gray-200"
                >
                  Technology (15)
                </a>
                <a
                  href="#"
                  className="block py-2 px-4 bg-gray-100 rounded-md hover:bg-gray-200"
                >
                  Health (10)
                </a>
                <a
                  href="#"
                  className="block py-2 px-4 bg-gray-100 rounded-md hover:bg-gray-200"
                >
                  Food Science (7)
                </a>
              </div>
            </div>

            {/* Newsletter */}
            <div className="bg-secondary text-white rounded-lg p-6 shadow-md">
              <h3 className="text-xl font-bold mb-4">
                Subscribe to Our Newsletter
              </h3>
              <p className="mb-4">
                Get the latest insights on AI in food tech and sustainable
                nutrition.
              </p>
              <input
                type="email"
                placeholder="Your email address"
                className="w-full py-2 px-4 rounded-md mb-3 text-gray-800 focus:outline-none"
              />
              <button className="w-full bg-accent hover:bg-yellow-600 text-white font-semibold py-2 px-4 rounded-md transition duration-300">
                Subscribe
              </button>
            </div>
          </div>
        </div>
      </div>
    </article>
  );
}

export default BlogPost;
